import { useState, useEffect } from "react";
import styled, { keyframes } from "styled-components";
import db from "../../config/firebase.config";
import {
    grantVerification,
    revokeVerification,
    subscribeVerificationRequests,
    processVerificationRequest,
} from "../../utils/verified";
import { useVerified } from "../../context/VerifiedContext";
import { VerificationBadge } from "../common/VerificationBadge";

const TABS = ["Verify Users", "Pending Requests", "Verified Users"];

export const AdminPanel = () => {
    const { isSuperAdmin, userProfile } = useVerified();
    const adminName = userProfile?.displayName || "scrubsmedina";

    const [activeTab, setActiveTab] = useState(0);
    const [searchQuery, setSearchQuery] = useState("");
    const [searchResults, setSearchResults] = useState([]);
    const [searching, setSearching] = useState(false);
    const [pendingRequests, setPendingRequests] = useState([]);
    const [verifiedUsers, setVerifiedUsers] = useState([]);
    const [toast, setToast] = useState(null);

    // Subscribe to pending verification requests
    useEffect(() => {
        const unsub = subscribeVerificationRequests(setPendingRequests);
        return unsub;
    }, []);

    // Subscribe to all verified users
    useEffect(() => {
        const unsub = db.collection("verifiedUsers").onSnapshot((snap) => {
            const list = [];
            snap.forEach((d) => list.push({ id: d.id, ...d.data() }));
            setVerifiedUsers(list);
        });
        return unsub;
    }, []);

    const showToast = (msg, type = "success") => {
        setToast({ msg, type });
        setTimeout(() => setToast(null), 3500);
    };

    const handleSearch = async () => {
        if (!searchQuery.trim()) return;
        setSearching(true);
        try {
            // Search by displayName (case-insensitive approximation)
            const q = searchQuery.toLowerCase();
            const snap = await db.collection("users").get();
            const results = [];
            snap.forEach((d) => {
                const data = d.data();
                const name = (data.displayName || "").toLowerCase();
                const email = (data.email || "").toLowerCase();
                if (name.includes(q) || email.includes(q)) {
                    results.push({ id: d.id, ...data });
                }
            });
            setSearchResults(results);
        } catch (e) {
            showToast("Search failed. Try again.", "error");
        } finally {
            setSearching(false);
        }
    };

    const handleGrant = async (targetUser, badgeType) => {
        try {
            await grantVerification(targetUser.id, badgeType, adminName, targetUser);
            showToast(`✅ ${targetUser.displayName || "User"} verified as ${badgeType} badge!`);
            // Refresh search
            await handleSearch();
        } catch (e) {
            showToast("Failed to grant verification.", "error");
        }
    };

    const handleRevoke = async (targetUser) => {
        try {
            await revokeVerification(targetUser.id || targetUser.uid, adminName);
            showToast(`🚫 Verification revoked for ${targetUser.name || targetUser.displayName || "user"}.`);
        } catch (e) {
            showToast("Failed to revoke verification.", "error");
        }
    };

    const handleProcessRequest = async (req, status, badgeType = "blue") => {
        try {
            await processVerificationRequest(
                req.id,
                req.uid,
                status,
                badgeType,
                adminName,
                { displayName: req.userName, email: req.userEmail, photoURL: req.userPhoto }
            );
            showToast(
                status === "APPROVED"
                    ? `✅ ${req.userName} approved!`
                    : `❌ ${req.userName} rejected.`,
                status === "APPROVED" ? "success" : "error"
            );
        } catch (e) {
            showToast("Action failed. Try again.", "error");
        }
    };

    if (!isSuperAdmin) {
        return (
            <Wrap>
                <div style={{ textAlign: "center", padding: "4rem", color: "#e53935" }}>
                    ⛔ Access Denied — Super Administrators only.
                </div>
            </Wrap>
        );
    }

    const pendingCount = pendingRequests.filter((r) => r.status === "PENDING").length;

    return (
        <Wrap>
            {toast && <Toast $type={toast.type}>{toast.msg}</Toast>}

            <Header>
                <div className="header-left">
                    <ShieldIcon />
                    <div>
                        <h1>Admin Panel</h1>
                        <p>Manage verifications · Fellora Super Administrator</p>
                    </div>
                </div>
                <div className="stats">
                    <StatCard $color="#1DA1F2">
                        <span className="num">{verifiedUsers.length}</span>
                        <span className="label">Verified</span>
                    </StatCard>
                    <StatCard $color="#c9a063">
                        <span className="num">{pendingCount}</span>
                        <span className="label">Pending</span>
                    </StatCard>
                </div>
            </Header>

            <TabBar>
                {TABS.map((tab, i) => (
                    <TabBtn
                        key={tab}
                        $active={activeTab === i}
                        onClick={() => setActiveTab(i)}
                    >
                        {tab}
                        {i === 1 && pendingCount > 0 && (
                            <NotifDot>{pendingCount}</NotifDot>
                        )}
                    </TabBtn>
                ))}
            </TabBar>

            <Content>
                {/* ── TAB 0: Verify Users ── */}
                {activeTab === 0 && (
                    <div>
                        <SectionTitle>Search &amp; Verify a Fellow</SectionTitle>
                        <SearchRow>
                            <input
                                type="text"
                                placeholder="Search by name or email…"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                            />
                            <button onClick={handleSearch} disabled={searching}>
                                {searching ? "Searching…" : "Search"}
                            </button>
                        </SearchRow>

                        {searchResults.length === 0 && searchQuery && !searching && (
                            <EmptyMsg>No users found for "{searchQuery}"</EmptyMsg>
                        )}

                        <UserGrid>
                            {searchResults.map((u) => (
                                <UserCard key={u.id}>
                                    <div className="card-left">
                                        <img
                                            src={u.photoURL || "https://cdn.pixabay.com/photo/2016/08/08/09/17/avatar-1577909_960_720.png"}
                                            alt={u.displayName}
                                        />
                                        <div className="info">
                                            <div className="name">
                                                {u.displayName || "Fellow"}
                                                {u.verified && (
                                                    <VerificationBadge type={u.verificationType || "blue"} size="sm" />
                                                )}
                                            </div>
                                            <div className="email">{u.email}</div>
                                            {u.verified ? (
                                                <StatusPill $type={u.verificationType || "blue"}>
                                                    ✓ {u.verificationType === "gold" ? "Gold" : "Blue"} Verified
                                                </StatusPill>
                                            ) : (
                                                <StatusPill $type="none">Not Verified</StatusPill>
                                            )}
                                        </div>
                                    </div>
                                    <div className="actions">
                                        {!u.verified && (
                                            <>
                                                <ActionBtn $color="#1DA1F2" onClick={() => handleGrant(u, "blue")}>
                                                    🔵 Blue Badge
                                                </ActionBtn>
                                                <ActionBtn $color="#c9a063" onClick={() => handleGrant(u, "gold")}>
                                                    🥇 Gold Badge
                                                </ActionBtn>
                                            </>
                                        )}
                                        {u.verified && (
                                            <>
                                                {u.verificationType === "blue" && (
                                                    <ActionBtn $color="#c9a063" onClick={() => handleGrant(u, "gold")}>
                                                        → Gold
                                                    </ActionBtn>
                                                )}
                                                {u.verificationType === "gold" && (
                                                    <ActionBtn $color="#1DA1F2" onClick={() => handleGrant(u, "blue")}>
                                                        → Blue
                                                    </ActionBtn>
                                                )}
                                                <ActionBtn $color="#e53935" onClick={() => handleRevoke(u)}>
                                                    Revoke
                                                </ActionBtn>
                                            </>
                                        )}
                                    </div>
                                </UserCard>
                            ))}
                        </UserGrid>
                    </div>
                )}

                {/* ── TAB 1: Pending Requests ── */}
                {activeTab === 1 && (
                    <div>
                        <SectionTitle>Verification Requests</SectionTitle>
                        {pendingRequests.filter((r) => r.status === "PENDING").length === 0 && (
                            <EmptyMsg>🎉 No pending requests.</EmptyMsg>
                        )}
                        <UserGrid>
                            {pendingRequests
                                .filter((r) => r.status === "PENDING")
                                .map((req) => (
                                    <UserCard key={req.id}>
                                        <div className="card-left">
                                            <img
                                                src={req.userPhoto || "https://cdn.pixabay.com/photo/2016/08/08/09/17/avatar-1577909_960_720.png"}
                                                alt={req.userName}
                                            />
                                            <div className="info">
                                                <div className="name">{req.userName}</div>
                                                <div className="email">{req.userEmail}</div>
                                                <StatusPill $type="pending">
                                                    ⏳ {req.requestedBadge === "gold" ? "Gold" : "Blue"} Badge Requested
                                                </StatusPill>
                                                {req.reason && (
                                                    <div className="reason">"{req.reason}"</div>
                                                )}
                                                <div className="submitted">
                                                    Submitted: {new Date(req.submittedAt).toLocaleDateString()}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="actions">
                                            <ActionBtn
                                                $color="#22c55e"
                                                onClick={() => handleProcessRequest(req, "APPROVED", req.requestedBadge || "blue")}
                                            >
                                                ✅ Approve
                                            </ActionBtn>
                                            <ActionBtn
                                                $color="#e53935"
                                                onClick={() => handleProcessRequest(req, "REJECTED")}
                                            >
                                                ❌ Reject
                                            </ActionBtn>
                                        </div>
                                    </UserCard>
                                ))}
                        </UserGrid>
                    </div>
                )}

                {/* ── TAB 2: Verified Users ── */}
                {activeTab === 2 && (
                    <div>
                        <SectionTitle>All Verified Fellows ({verifiedUsers.length})</SectionTitle>
                        {verifiedUsers.length === 0 && (
                            <EmptyMsg>No verified users yet.</EmptyMsg>
                        )}
                        <UserGrid>
                            {verifiedUsers.map((u) => (
                                <UserCard key={u.id}>
                                    <div className="card-left">
                                        <div className="initials-avatar">
                                            {(u.name || u.email || "?").charAt(0).toUpperCase()}
                                        </div>
                                        <div className="info">
                                            <div className="name">
                                                {u.name || "Fellow"}
                                                <VerificationBadge type={u.verificationType || "blue"} size="sm" />
                                            </div>
                                            <div className="email">{u.email}</div>
                                            <StatusPill $type={u.verificationType || "blue"}>
                                                ✓ {u.verificationType === "gold" ? "Gold" : "Blue"} Badge
                                            </StatusPill>
                                            {u.verifiedDate && (
                                                <div className="submitted">
                                                    Since: {new Date(u.verifiedDate).toLocaleDateString()}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    <div className="actions">
                                        <ActionBtn $color="#e53935" onClick={() => handleRevoke(u)}>
                                            Revoke
                                        </ActionBtn>
                                    </div>
                                </UserCard>
                            ))}
                        </UserGrid>
                    </div>
                )}
            </Content>
        </Wrap>
    );
};

// ─── Icons ───────────────────────────────────────────────────────────────────
const ShieldIcon = () => (
    <svg width="36" height="36" viewBox="0 0 24 24" fill="#7c3aed">
        <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm0 4l5 2.18V11c0 3.5-2.33 6.79-5 7.93C9.33 17.79 7 14.5 7 11V7.18L12 5z" />
    </svg>
);

// ─── Styled Components ───────────────────────────────────────────────────────
const slideDown = keyframes`
    from { transform: translateY(-20px); opacity: 0; }
    to   { transform: translateY(0);     opacity: 1; }
`;

const Wrap = styled.div`
    min-height: 100vh;
    background-color: #faf7f2;
    padding: 24px 20px 60px;
    max-width: 1000px;
    margin: 0 auto;
    position: relative;
`;

const Toast = styled.div`
    position: fixed;
    top: 72px;
    left: 50%;
    transform: translateX(-50%);
    background: ${(p) => (p.$type === "error" ? "#e53935" : "#0a1428")};
    color: white;
    padding: 12px 24px;
    border-radius: 30px;
    font-size: 18px;
    font-weight: 700;
    z-index: 9999;
    box-shadow: 0 8px 24px rgba(0,0,0,0.18);
    animation: ${slideDown} 0.3s ease;
`;

const Header = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 16px;
    background: #ffffff;
    border: 1px solid rgba(201,160,99,0.25);
    border-radius: 14px;
    padding: 20px 24px;
    margin-bottom: 20px;
    box-shadow: 0 4px 20px rgba(10,20,40,0.06);

    .header-left {
        display: flex;
        align-items: center;
        gap: 14px;

        h1 {
            font-size: 28px;
            font-weight: 700;
            color: #0a1428;
            margin: 0;
        }
        p {
            font-size: 17px;
            color: #888;
            margin: 2px 0 0;
        }
    }

    .stats {
        display: flex;
        gap: 12px;
    }
`;

const StatCard = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    background: ${(p) => p.$color}18;
    border: 1.5px solid ${(p) => p.$color}44;
    border-radius: 10px;
    padding: 10px 20px;
    min-width: 70px;

    .num {
        font-size: 26px;
        font-weight: 700;
        color: ${(p) => p.$color};
    }
    .label {
        font-size: 15px;
        color: #888;
        font-weight: 600;
    }
`;

const TabBar = styled.div`
    display: flex;
    gap: 8px;
    margin-bottom: 20px;
    border-bottom: 2px solid rgba(201,160,99,0.2);
    padding-bottom: 12px;
`;

const TabBtn = styled.button`
    position: relative;
    background: ${(p) => (p.$active ? "#0a1428" : "#ffffff")};
    color: ${(p) => (p.$active ? "#ffffff" : "#555")};
    border: 1.5px solid ${(p) => (p.$active ? "#0a1428" : "rgba(201,160,99,0.35)")};
    border-radius: 20px;
    padding: 8px 18px;
    font-size: 18px;
    font-weight: 700;
    cursor: pointer;
    transition: all 0.2s;

    &:hover {
        background: ${(p) => (p.$active ? "#0a1428" : "#f5efe6")};
    }
`;

const NotifDot = styled.span`
    position: absolute;
    top: -4px;
    right: -4px;
    background: #e53935;
    color: white;
    font-size: 12px;
    font-weight: 700;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 2px solid white;
`;

const Content = styled.div`
    background: #ffffff;
    border: 1px solid rgba(201,160,99,0.25);
    border-radius: 14px;
    padding: 24px;
    box-shadow: 0 4px 20px rgba(10,20,40,0.06);
`;

const SectionTitle = styled.h2`
    font-size: 22px;
    font-weight: 700;
    color: #0a1428;
    margin: 0 0 16px;
`;

const SearchRow = styled.div`
    display: flex;
    gap: 10px;
    margin-bottom: 20px;

    input {
        flex: 1;
        padding: 12px 16px;
        border: 1.5px solid rgba(201,160,99,0.4);
        border-radius: 10px;
        font-size: 19px;
        outline: none;
        transition: border-color 0.2s;

        &:focus { border-color: #1DA1F2; }
    }

    button {
        padding: 12px 22px;
        background: #0a1428;
        color: white;
        border: none;
        border-radius: 10px;
        font-size: 19px;
        font-weight: 700;
        cursor: pointer;
        transition: opacity 0.2s;

        &:hover { opacity: 0.85; }
        &:disabled { opacity: 0.5; cursor: not-allowed; }
    }
`;

const EmptyMsg = styled.div`
    text-align: center;
    font-size: 20px;
    color: #999;
    padding: 2rem;
    font-style: italic;
`;

const UserGrid = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

const UserCard = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 16px;
    padding: 14px 16px;
    background: #faf7f2;
    border: 1px solid rgba(201,160,99,0.25);
    border-radius: 10px;
    transition: box-shadow 0.2s;

    &:hover { box-shadow: 0 4px 14px rgba(10,20,40,0.08); }

    .card-left {
        display: flex;
        align-items: flex-start;
        gap: 12px;
        flex: 1;

        img, .initials-avatar {
            width: 48px;
            height: 48px;
            border-radius: 50%;
            object-fit: cover;
            border: 1.5px solid rgba(201,160,99,0.4);
            flex-shrink: 0;
        }

        .initials-avatar {
            background: linear-gradient(135deg, #c9a063, #e8c47d);
            color: white;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 22px;
            font-weight: 700;
        }

        .info {
            display: flex;
            flex-direction: column;
            gap: 3px;

            .name {
                font-size: 21px;
                font-weight: 700;
                color: #0a1428;
                display: flex;
                align-items: center;
                gap: 4px;
            }
            .email {
                font-size: 17px;
                color: #888;
            }
            .reason {
                font-size: 17px;
                color: #555;
                font-style: italic;
                max-width: 300px;
            }
            .submitted {
                font-size: 15px;
                color: #bbb;
            }
        }
    }

    .actions {
        display: flex;
        flex-direction: column;
        gap: 6px;
        flex-shrink: 0;
    }
`;

const StatusPill = styled.span`
    display: inline-flex;
    align-items: center;
    font-size: 15px;
    font-weight: 700;
    padding: 2px 8px;
    border-radius: 20px;
    background: ${(p) =>
        p.$type === "blue" ? "#e8f5fe" :
        p.$type === "gold" ? "#fdf5e6" :
        p.$type === "pending" ? "#fff8e1" :
        "#f5f5f5"};
    color: ${(p) =>
        p.$type === "blue" ? "#1DA1F2" :
        p.$type === "gold" ? "#c9a063" :
        p.$type === "pending" ? "#e6a817" :
        "#999"};
`;

const ActionBtn = styled.button`
    padding: 6px 14px;
    border: 1.5px solid ${(p) => p.$color};
    border-radius: 20px;
    background: transparent;
    color: ${(p) => p.$color};
    font-size: 17px;
    font-weight: 700;
    cursor: pointer;
    white-space: nowrap;
    transition: all 0.18s;

    &:hover {
        background: ${(p) => p.$color};
        color: white;
    }
`;

export default AdminPanel;
